/**
 * TagTransferForm - Send money by @tag with user preview and search
 */
import { useState, useEffect, useCallback } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Loader2, CheckCircle, XCircle, AtSign, Search } from 'lucide-react';
import api from '../../lib/api';
import { debounce } from 'lodash';

const TagTransferForm = ({ recipientTag, setRecipientTag, onRecipientResolved }) => {
  const [checking, setChecking] = useState(false);
  const [resolvedUser, setResolvedUser] = useState(null);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // Debounced tag resolution or search
  const resolveOrSearch = useCallback(
    debounce(async (tag) => {
      if (!tag || tag.length < 2) {
        setResolvedUser(null);
        setError(null);
        setSearchResults([]);
        onRecipientResolved?.(null);
        return;
      }

      setChecking(true);
      setError(null);

      const cleanTag = tag.replace('@', '');
      
      try {
        // First try exact resolution
        const { data } = await api.get(`/v1/tags/${cleanTag}`);
        setResolvedUser(data);
        setSearchResults([]);
        setShowSearch(false);
        onRecipientResolved?.(data);
      } catch (err) {
        if (err.response?.status === 404) {
          // Not found - try search instead
          try {
            const searchRes = await api.get(`/v1/tags/search/${cleanTag}?limit=5`);
            setSearchResults(searchRes.data || []);
            setShowSearch(searchRes.data?.length > 0);
            setResolvedUser(null);
            setError(searchRes.data?.length === 0 ? 'No users found' : null);
            onRecipientResolved?.(null);
          } catch {
            setSearchResults([]);
            setShowSearch(false);
            setError('No users found');
            onRecipientResolved?.(null);
          }
        } else {
          setError('Error resolving tag');
          onRecipientResolved?.(null);
        }
        setResolvedUser(null);
      } finally {
        setChecking(false);
      }
    }, 400),
    [onRecipientResolved]
  );

  useEffect(() => {
    const cleanTag = recipientTag?.replace('@', '');
    if (cleanTag && cleanTag.length >= 2) {
      resolveOrSearch(cleanTag);
    } else {
      setResolvedUser(null);
      setSearchResults([]);
      setShowSearch(false);
      setError(null);
      onRecipientResolved?.(null);
    }
  }, [recipientTag, resolveOrSearch, onRecipientResolved]);

  const handleInputChange = (e) => {
    let value = e.target.value;
    // Auto-prefix with @ if not present
    if (value && !value.startsWith('@')) {
      value = '@' + value;
    }
    // Only allow alphanumeric and underscore after @
    value = value.replace(/[^@a-zA-Z0-9_]/g, '');
    setRecipientTag(value);
  };

  const selectSearchResult = (result) => {
    setRecipientTag(`@${result.tag}`);
    setResolvedUser(result);
    setSearchResults([]);
    setShowSearch(false);
    onRecipientResolved?.(result);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-3">
      <Label htmlFor="recipient-tag" className="flex items-center gap-2">
        <AtSign className="h-4 w-4 text-emerald-500" />
        Send to @tag
      </Label>
      <div className="relative">
        <Input
          id="recipient-tag"
          type="text"
          placeholder="@username"
          value={recipientTag}
          onChange={handleInputChange}
          className={`h-12 rounded-xl pl-4 pr-10 ${
            resolvedUser ? 'border-emerald-500 bg-emerald-500/5' : 
            error ? 'border-red-500 bg-red-500/5' : ''
          }`}
          data-testid="recipient-tag-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {checking && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
          {!checking && resolvedUser && <CheckCircle className="h-5 w-5 text-emerald-500" />}
          {!checking && error && <XCircle className="h-5 w-5 text-red-500" />}
        </div>
      </div>

      {/* Search Results */}
      {showSearch && searchResults.length > 0 && (
        <Card className="border-blue-500/30 bg-blue-500/5" data-testid="search-results">
          <CardContent className="p-2">
            <p className="text-xs text-muted-foreground px-2 py-1 flex items-center gap-1">
              <Search className="h-3 w-3" />
              Did you mean:
            </p>
            <div className="space-y-1">
              {searchResults.map((result) => (
                <button
                  key={result.tag}
                  onClick={() => selectSearchResult(result)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-blue-500/10 transition-colors text-left"
                >
                  <Avatar className="h-8 w-8">
                    {result.avatar_url ? (
                      <img src={result.avatar_url} alt={result.display_name} />
                    ) : (
                      <AvatarFallback className="bg-blue-600 text-white text-xs">
                        {getInitials(result.display_name)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium">{result.display_name}</p>
                    <p className="text-xs text-blue-600 font-mono">{result.tag_display}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Preview Card */}
      {resolvedUser && (
        <Card className="border-emerald-500/50 bg-emerald-500/5" data-testid="resolved-user-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 border-2 border-emerald-500">
                {resolvedUser.avatar_url ? (
                  <img src={resolvedUser.avatar_url} alt={resolvedUser.display_name} />
                ) : (
                  <AvatarFallback className="bg-emerald-600 text-white font-bold">
                    {getInitials(resolvedUser.display_name)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1">
                <p className="font-semibold text-foreground">{resolvedUser.display_name}</p>
                <p className="text-sm text-emerald-600 font-mono">{resolvedUser.tag_display}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Account ID</p>
                <p className="text-sm font-mono">{resolvedUser.masked_wallet_id}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Message */}
      {error && !checking && !showSearch && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          <XCircle className="h-4 w-4" />
          {error}
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        Enter a ParachPay @tag or search for users
      </p>
    </div>
  );
};

export default TagTransferForm;
